import React, { useState, useEffect } from "react";
import axios from "axios";
import Datetime from "react-datetime";
import moment from "moment";
import "react-datetime/css/react-datetime.css";

const REQUIRED_FIELDS = ["name", "phoneNumber", "email", "message", "dateTime"];

const RequestCallback = () => {
    const [form, setForm] = useState({
        name: "",
        phoneNumber: "",
        email: "",
        message: "",
        dateTime: null,
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [formMessage, setFormMessage] = useState("");
    const [formMessageType, setFormMessageType] = useState(""); // success | error
    const [API_URL, setAPI_URL] = useState(""); // dynamically loaded from public/config.json

    /** --- Load API URL from public/config.json --- */
    useEffect(() => {
        fetch("/config.json")
            .then((res) => res.json())
            .then((data) =>
                setAPI_URL(`${data.API_BASE_URL}/service/requestACallback`)
            )
            .catch((err) =>
                console.error("Failed to load config.json", err)
            );
    }, []);

    /** --- Validators --- */
    const validators = {
        name: (v) => (v.trim() ? null : "Name is required."),
        phoneNumber: (v) =>
            /^\d{10}$/.test(v) ? null : "Phone number must be exactly 10 digits.",
        email: (v) =>
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Enter a valid email address.",
        message: (v) => (v.trim() ? null : "Message is required."),
        dateTime: (v) => {
            if (!v || !moment(v).isValid()) return "Please select a valid date and time.";
            if (moment(v).isBefore(moment())) return "Please choose a future time.";
            return null;
        },
    };

    /** --- Track form validity --- */
    useEffect(() => {
        const hasEmpty = REQUIRED_FIELDS.some((f) => !form[f]);
        const hasError = Object.values(errors).some(Boolean);
        setIsFormValid(!hasEmpty && !hasError);
    }, [form, errors]);

    /** --- Validation helpers --- */
    const validateField = (name, value) => {
        if (!validators[name]) return;
        const err = validators[name](value);
        setErrors((prev) => ({ ...prev, [name]: err }));
    };

    const validateForm = () => {
        const newErrors = {};
        REQUIRED_FIELDS.forEach((f) => {
            newErrors[f] = validators[f](form[f]);
        });
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    /** --- Input change --- */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    /** --- Datetime change --- */
    const handleDateChange = (value) => {
        if (!moment(value).isValid()) return;
        const finalValue = moment(value).toDate();
        setForm((prev) => ({ ...prev, dateTime: finalValue }));
        validateField("dateTime", finalValue);
        setTimeout(() => document.activeElement?.blur(), 100);
    };

    const handleDateBlur = () => {
        validateField("dateTime", form.dateTime);
    };

    /** --- Reset form --- */
    const resetForm = () => {
        setForm({
            name: "",
            phoneNumber: "",
            email: "",
            message: "",
            dateTime: null,
        });
        setErrors({});
        setFormMessage("");
        setFormMessageType("");
    };

    /** --- Submit --- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting || !API_URL) return; // wait until API_URL is loaded

        setFormMessage("");
        setFormMessageType("");

        if (!validateForm()) {
            setFormMessage("Please fix the highlighted fields.");
            setFormMessageType("error");
            // Hide message after 3 seconds
            setTimeout(() => setFormMessage(""), 5000);
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                type: "requestACallBack",
                ...form,
                message: form.message.trim(),
                dateTime: moment(form.dateTime).format("YYYY-MM-DD HH:mm:ss"),
            };

            const response = await axios.post(API_URL, payload, {
                headers: { "Content-Type": "application/json" },
            });

            resetForm();
            setFormMessage(response.data?.message || "Callback request submitted successfully!");
            setFormMessageType("success");
            // Hide message after 3 seconds
            setTimeout(() => setFormMessage(""), 5000);
        } catch (err) {
            if (err.response)
                setFormMessage(err.response.data?.message || "Server error occurred.");
            else if (err.request)
                setFormMessage("Network error. Please check your connection.");
            else setFormMessage(err.message || "An unexpected error occurred.");

            setFormMessageType("error");
            // Hide message after 3 seconds
            setTimeout(() => setFormMessage(""), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    /** --- Render --- */
    return (
        <form onSubmit={handleSubmit} className="container p-3">
            <h5 className="text-center mb-4" style={{ color: "var(--brand-color)" }}>
                Fill the form below to request a callback
            </h5>

            {formMessage && (
                <div
                    className={`alert text-center ${
                        formMessageType === "success" ? "alert-success" : "alert-danger"
                    }`}
                >
                    {formMessage}
                </div>
            )}

            <div className="row g-3">
                <div className="col-12 mt-2">
                    <input
                        type="text"
                        name="name"
                        placeholder="Your Full Name *"
                        value={form.name}
                        onChange={handleChange}
                        onBlur={(e) => validateField("name", e.target.value)}
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                <div className="col-12 mt-2">
                    <input
                        type="text"
                        name="phoneNumber"
                        placeholder="Phone Number *"
                        value={form.phoneNumber}
                        onChange={handleChange}
                        onBlur={(e) => validateField("phoneNumber", e.target.value)}
                        className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                    />
                    {errors.phoneNumber && (
                        <div className="invalid-feedback">{errors.phoneNumber}</div>
                    )}
                </div>

                <div className="col-12 mt-2">
                    <input
                        type="email"
                        name="email"
                        placeholder="Email Address *"
                        value={form.email}
                        onChange={handleChange}
                        onBlur={(e) => validateField("email", e.target.value)}
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="col-12 mt-2">
                    <Datetime
                        value={form.dateTime}
                        onChange={handleDateChange}
                        onBlur={handleDateBlur}
                        dateFormat="YYYY-MM-DD"
                        timeFormat="hh:mm A"
                        inputProps={{
                            placeholder: "Preferred Date & Time *",
                            className: `form-control ${errors.dateTime ? "is-invalid" : ""}`,
                        }}
                        isValidDate={(current) =>
                            current.isAfter(moment().subtract(1, "minutes"))
                        }
                    />
                    {errors.dateTime && (
                        <div className="invalid-feedback d-block">{errors.dateTime}</div>
                    )}
                </div>

                <div className="col-12 mt-2">
          <textarea
              name="message"
              rows="3"
              placeholder="Your Message *"
              value={form.message}
              onChange={handleChange}
              onBlur={(e) => validateField("message", e.target.value)}
              className={`form-control ${errors.message ? "is-invalid" : ""}`}
          />
                    {errors.message && (
                        <div className="invalid-feedback d-block">{errors.message}</div>
                    )}
                </div>
            </div>

            <div className="text-center mt-4">
                <button
                    type="submit"
                    className="btn btn-brand"
                    disabled={!isFormValid || isSubmitting || !API_URL}
                    aria-busy={isSubmitting}
                >
                    {isSubmitting ? "Submitting..." : "Request Callback"}
                </button>
            </div>
        </form>
    );
};

export default RequestCallback;
